// Patch ReactDOM with polyfills after it's loaded
let ReactDOM, ReactDOMClient;

// Try direct imports first
try {
  ReactDOM = require("react-dom");
  console.log("Successfully loaded react-dom");
} catch (e) {
  console.warn("Failed to load react-dom:", e.message);
  ReactDOM = {};
}

try {
  ReactDOMClient = require("react-dom/client");
  console.log("Successfully loaded react-dom/client:", ReactDOMClient);
} catch (e) {
  console.warn("Failed to load react-dom/client:", e.message);
  ReactDOMClient = {};
}

// If direct imports failed, try with eval to bypass webpack
if (!ReactDOM || Object.keys(ReactDOM).length === 0) {
  try {
    ReactDOM = eval('require("react-dom")');
    console.log("Loaded react-dom via eval");
  } catch (e) {
    console.warn("Failed to load react-dom via eval:", e.message);
  }
}

if (!ReactDOMClient || Object.keys(ReactDOMClient).length === 0) {
  try {
    ReactDOMClient = eval('require("react-dom/client")');
    console.log("Loaded react-dom/client via eval");
  } catch (e) {
    console.warn("Failed to load react-dom/client via eval:", e.message);
  }
}

// Fallback to window globals if available
if (
  (!ReactDOM || Object.keys(ReactDOM).length === 0) &&
  typeof window !== "undefined" &&
  window.ReactDOM
) {
  ReactDOM = window.ReactDOM;
  console.log("Using window.ReactDOM");
}

if (
  (!ReactDOMClient || Object.keys(ReactDOMClient).length === 0) &&
  typeof window !== "undefined" &&
  window.ReactDOM
) {
  ReactDOMClient = window.ReactDOM;
  console.log("Using window.ReactDOM for client");
}

// findDOMNode polyfill for React 19
let findDOMNodeWarningShown = false;

function findDOMNodePolyfill(component) {
  if (typeof window === "undefined") {
    return null;
  }

  // Show warning only once to avoid console spam
  if (!findDOMNodeWarningShown) {
    console.warn(
      "findDOMNode is deprecated and was removed in React 19. Consider using refs instead."
    );
    findDOMNodeWarningShown = true;
  }

  // If component is null or undefined
  if (!component) {
    return null;
  }

  // If component is already a DOM node
  if (component && typeof component === "object" && component.nodeType) {
    return component;
  }

  // Try to get DOM node from React fiber (React internals)
  if (component && component._reactInternals) {
    let fiber = component._reactInternals;
    while (fiber) {
      if (fiber.stateNode && fiber.stateNode.nodeType) {
        return fiber.stateNode;
      }
      fiber = fiber.child;
    }
  }

  // Try legacy React internals
  if (component && component._reactInternalFiber) {
    let fiber = component._reactInternalFiber;
    while (fiber) {
      if (fiber.stateNode && fiber.stateNode.nodeType) {
        return fiber.stateNode;
      }
      fiber = fiber.child;
    }
  }

  // If component has a ref
  if (component && component.current && component.current.nodeType) {
    return component.current;
  }

  // Fallback: return null
  if (!findDOMNodeWarningShown) {
    console.warn("findDOMNode polyfill: Could not find DOM node for component", component);
  }
  return null;
}

// Legacy render method for compatibility - React 19 removed render
let renderWarningShown = false;

function renderPolyfill(element, container, callback) {
  // Show warning only once to avoid console spam
  if (!renderWarningShown) {
    console.warn("ReactDOM.render is deprecated. Using createRoot instead.");
    renderWarningShown = true;
  }
  if (
    container &&
    element &&
    (ReactDOMClient.createRoot ||
      (typeof window !== "undefined" && window.ReactDOM && window.ReactDOM.createRoot))
  ) {
    const createRoot = ReactDOMClient.createRoot || (window.ReactDOM && window.ReactDOM.createRoot);
    if (createRoot) {
      const root = createRoot(container);
      root.render(element);
      if (callback) callback();
    }
  }
}

// Create the patched ReactDOM object
const PatchedReactDOM = {
  // Add our polyfills
  findDOMNode: findDOMNodePolyfill,
  render: renderPolyfill,
};

// Safely spread ReactDOM properties
if (ReactDOM && typeof ReactDOM === "object") {
  for (const key in ReactDOM) {
    if (ReactDOM.hasOwnProperty(key) && !PatchedReactDOM.hasOwnProperty(key)) {
      PatchedReactDOM[key] = ReactDOM[key];
    }
  }
}

// Safely spread ReactDOMClient properties
if (ReactDOMClient && typeof ReactDOMClient === "object") {
  for (const key in ReactDOMClient) {
    if (ReactDOMClient.hasOwnProperty(key) && !PatchedReactDOM.hasOwnProperty(key)) {
      PatchedReactDOM[key] = ReactDOMClient[key];
    }
  }
}

// Ensure these critical methods are available
if (!PatchedReactDOM.createPortal) {
  PatchedReactDOM.createPortal =
    (ReactDOM && ReactDOM.createPortal) ||
    (() => {
      console.warn("createPortal not available");
      return null;
    });
}

if (!PatchedReactDOM.flushSync) {
  PatchedReactDOM.flushSync =
    (ReactDOM && ReactDOM.flushSync) ||
    ((fn) => {
      console.warn("flushSync not available, executing function directly");
      return fn && fn();
    });
}

if (!PatchedReactDOM.createRoot) {
  // Try multiple sources for createRoot
  const createRootFn =
    (ReactDOMClient && ReactDOMClient.createRoot) ||
    (ReactDOM && ReactDOM.createRoot) ||
    (typeof window !== "undefined" && window.ReactDOM && window.ReactDOM.createRoot);

  console.log("Looking for createRoot:", {
    ReactDOMClient: ReactDOMClient,
    "ReactDOMClient.createRoot": ReactDOMClient && ReactDOMClient.createRoot,
    "ReactDOM.createRoot": ReactDOM && ReactDOM.createRoot,
    "window.ReactDOM": typeof window !== "undefined" ? window.ReactDOM : "no window",
    createRootFn: createRootFn,
  });

  if (createRootFn) {
    PatchedReactDOM.createRoot = createRootFn;
    console.log("Successfully assigned createRoot function");
  } else {
    console.error("createRoot not available - React 19 is required");
    PatchedReactDOM.createRoot = () => {
      console.error("createRoot called but not available");
      return null;
    };
  }
}

if (!PatchedReactDOM.hydrateRoot) {
  PatchedReactDOM.hydrateRoot =
    (ReactDOMClient && ReactDOMClient.hydrateRoot) ||
    (() => {
      console.warn("hydrateRoot not available");
      return null;
    });
}

// Also make them available on window.ReactDOM for legacy code
if (typeof window !== "undefined") {
  if (!window.ReactDOM) {
    window.ReactDOM = {};
  }
  Object.assign(window.ReactDOM, PatchedReactDOM);
}

// Handle different import patterns
// For: import ReactDOM from 'react-dom'
module.exports = PatchedReactDOM;

// For: import { findDOMNode, render, ... } from 'react-dom'
module.exports.findDOMNode = findDOMNodePolyfill;
module.exports.render = renderPolyfill;
module.exports.createPortal = PatchedReactDOM.createPortal;
module.exports.flushSync = PatchedReactDOM.flushSync;
module.exports.createRoot = PatchedReactDOM.createRoot;
module.exports.hydrateRoot = PatchedReactDOM.hydrateRoot;

// Export all other properties if ReactDOM is available
if (ReactDOM && typeof ReactDOM === "object") {
  for (const key in ReactDOM) {
    if (ReactDOM.hasOwnProperty(key) && !module.exports.hasOwnProperty(key)) {
      module.exports[key] = ReactDOM[key];
    }
  }
}

// Export all other properties if ReactDOMClient is available
if (ReactDOMClient && typeof ReactDOMClient === "object") {
  for (const key in ReactDOMClient) {
    if (ReactDOMClient.hasOwnProperty(key) && !module.exports.hasOwnProperty(key)) {
      module.exports[key] = ReactDOMClient[key];
    }
  }
}

// Default export for ES6
module.exports.default = PatchedReactDOM;
